import { useCallback, useEffect, useRef, useState } from "react";
import {
  getDbInventoryDcRoutingPacking,
  markInventoryDcRoutingPacking,
  packInventoryVisibleDcRoutingPacking,
} from "@services/api";
import { notifySuccess, notifyError } from "@components/ds/notify";

// Wraps GET /dc-routing-packing/v1/dbs/:db_id/inventory and
// POST /dc-routing-packing/v1/dbs/:db_id/inventory/pack-visible. The fetch is
// per-(db_id, route_id) — every route the DB subscribes to needs its own
// fetch since the backend filter scopes results that tightly.
//
// Today the screen calls this once for the DB's first route — wire-up to
// multi-route is left to the caller.

const useDbInventory = ({ db_id, route_id }) => {
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const reload = useCallback(async () => {
    if (!db_id || !route_id) {
      if (isMounted.current) setSkus([]);
      return;
    }
    setLoading(true);
    try {
      const res = await getDbInventoryDcRoutingPacking({ db_id, route_id });
      const list = Array.isArray(res?.data?.data?.skus)
        ? res.data.data.skus
        : [];
      if (isMounted.current) setSkus(list);
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load inventory";
      notifyError(msg);
      if (isMounted.current) setSkus([]);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [db_id, route_id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const packVisible = useCallback(
    async (orderItemIds) => {
      if (!db_id || !route_id) return false;
      const ids = Array.isArray(orderItemIds) ? orderItemIds : [];
      if (ids.length === 0) {
        notifyError("Nothing to pack");
        return false;
      }
      setSubmitting(true);
      try {
        await packInventoryVisibleDcRoutingPacking({
          db_id,
          route_id,
          order_item_ids: ids.map((id) => Number(id)),
        });
        notifySuccess(
          `Packed ${ids.length} item${ids.length === 1 ? "" : "s"}`,
        );
        await reload();
        return true;
      } catch (e) {
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to pack inventory";
        notifyError(msg);
        return false;
      } finally {
        if (isMounted.current) setSubmitting(false);
      }
    },
    [db_id, route_id, reload],
  );

  // SKU-level marking — server distributes the diff across the sku's
  // unpacked order_item_ids and fans out to the per-item kernel. When
  // `order_item_ids` is provided, the server intersects with the sku's
  // unpacked list so marking scopes to that customer's order_items only.
  const markSku = useCallback(
    async ({ sku_id, diff, order_item_ids }) => {
      if (!db_id || !route_id || !sku_id) return false;
      const totalRequested = Object.values(diff || {}).reduce(
        (a, b) => a + Number(b || 0),
        0,
      );
      if (totalRequested === 0) {
        notifyError("Nothing to mark");
        return false;
      }
      setSubmitting(true);
      try {
        const res = await markInventoryDcRoutingPacking({
          db_id,
          route_id,
          sku_id,
          diff,
          order_item_ids,
        });
        const data = res?.data?.data || {};
        const requested = data.requested_count ?? totalRequested;
        const marked = data.marked_count ?? 0;
        if (marked === requested) {
          notifySuccess(`Marked ${marked} item${marked === 1 ? "" : "s"}`);
        } else if (marked > 0) {
          notifyError(`Marked ${marked} of ${requested} (rest already marked)`);
        } else {
          notifyError("None could be marked — items may already be marked");
        }
        await reload();
        return marked > 0;
      } catch (e) {
        const msg =
          e?.response?.data?.error ||
          e?.response?.data?.message ||
          e?.message ||
          "Failed to mark inventory";
        notifyError(msg);
        return false;
      } finally {
        if (isMounted.current) setSubmitting(false);
      }
    },
    [db_id, route_id, reload],
  );

  return { skus, loading, submitting, reload, packVisible, markSku };
};

export default useDbInventory;
